import { nextTick, reactive, type Ref, watch } from 'vue';
import type { Option } from '@/modules/common/types/option.type.ts';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import { queryClient } from '@/tanstack-query.ts';
import { SegmentApi } from '@/modules/counterparty/api/segment.api.ts';
import { RegionApi } from '@/modules/counterparty/api/region.api.ts';
import { SegmentQueryOptions } from '@/modules/counterparty/query-options/segment.query-options.ts';
import { RegionQueryOptions } from '@/modules/counterparty/query-options/region.query-options.ts';
import { TimezoneQueryOptions } from '@/modules/counterparty/query-options/timezone.query-options.ts';
import type { CounterpartyMasterForm } from '@/modules/counterparty/counterparty-master/types/master-form.ts';
import type { Segment } from '@/modules/counterparty/types/segment.type.ts';
import type { Region } from '@/modules/counterparty/types/region.type.ts';
import type { DirectoryField } from '@/modules/counterparty/counterparty-master/consts/directory.const.ts';
import { resolveDirectoryFieldState } from '@/modules/counterparty/counterparty-master/consts/directory.const.ts';
import {
  createPendingDirectoryId,
  getPendingDirectoryList,
  getPendingRegion,
  getPendingSegment,
  isPendingDirectoryId,
  mergeDirectoryOptions,
  pruneUnusedPendingDirectory,
  removePendingDirectory,
  upsertPendingRegion,
  upsertPendingSegment,
} from '@/modules/counterparty/counterparty-master/utils/pending-directory.util.ts';
import { findDefaultTimezone } from '@/modules/counterparty/counterparty-master/utils/find-default-timezone.util.ts';
import { findDirectoryNameDuplicate } from '@/modules/counterparty/counterparty-master/utils/find-directory-name-duplicate.util.ts';
import { fetchDirectoryList } from '@/modules/counterparty/counterparty-master/utils/fetch-directory-list.util.ts';

export type { DirectoryField };
export type DirectoryFieldMode = 'add' | 'edit';

export type DirectoryFormPayload = {
  name: string;
  note: string;
  center?: string;
  tzId?: number;
};

type ComboboxField = {
  updateOptions?: () => Promise<void> | void;
  choose?: (code: number) => void;
};

type DirectoryModalState = {
  open: boolean;
  field: DirectoryField;
  mode: DirectoryFieldMode;
  name: string;
  note: string;
  center: string;
  tzId: number | null;
  itemId: number | null;
  loading: boolean;
};

const DIRECTORY_FIELDS = ['segment', 'region'] as const satisfies DirectoryField[];

function upsertListItem<T extends { id: number | string }>(
  old: T[] | undefined,
  id: number,
  item: T,
  mergeExisting = false
) {
  if (!old) {
    return [item];
  }

  const index = old.findIndex((entry) => Number(entry.id) === id);

  if (index < 0) {
    return [...old, item];
  }

  const current = old[index];

  if (!current) {
    return old;
  }

  const next = [...old];
  next[index] = mergeExisting ? { ...current, ...item } : item;
  return next;
}

export function useManagerDirectoryFields(
  formData: Ref<CounterpartyMasterForm>,
  formRef: { value: { getField: (name: string) => unknown } | null }
) {
  const modal = reactive<DirectoryModalState>({
    open: false,
    field: 'segment',
    mode: 'add',
    name: '',
    note: '',
    center: '',
    tzId: null,
    itemId: null,
    loading: false,
  });

  function getCombobox(field: DirectoryField) {
    return formRef.value?.getField(field) as ComboboxField | undefined;
  }

  for (const field of DIRECTORY_FIELDS) {
    watch(
      () => formData.value[field]?.code,
      (code) => {
        if (!pruneUnusedPendingDirectory(field, typeof code === 'number' ? code : null)) {
          return;
        }

        void getCombobox(field)?.updateOptions?.();
      }
    );
  }

  function getCachedDirectoryList(field: DirectoryField) {
    const cached =
      field === 'segment'
        ? (queryClient.getQueryData<Segment[]>(SegmentQueryOptions.getList().queryKey) ?? [])
        : (queryClient.getQueryData<Region[]>(RegionQueryOptions.getList().queryKey) ?? []);

    return [...cached, ...getPendingDirectoryList(field)];
  }

  function isNameTaken(field: DirectoryField, name: string, excludeId?: number | null) {
    return !!findDirectoryNameDuplicate(getCachedDirectoryList(field), name, excludeId);
  }

  function resetModalFields() {
    modal.name = '';
    modal.note = '';
    modal.center = '';
    modal.tzId = null;
    modal.itemId = null;
  }

  function startAdd(field: DirectoryField) {
    modal.field = field;
    modal.mode = 'add';
    resetModalFields();
    modal.open = true;
  }

  function startEdit(field: DirectoryField, option?: Option<number> | null) {
    const current = option ?? formData.value[field];

    if (!current || typeof current.code !== 'number') {
      return;
    }

    if (!option && !getCachedDirectoryList(field).length) {
      return;
    }

    modal.field = field;
    modal.mode = 'edit';
    modal.name = current.label.trim();
    modal.itemId = current.code;

    const pending = isPendingDirectoryId(current.code)
      ? field === 'segment'
        ? getPendingSegment(current.code)
        : getPendingRegion(current.code)
      : undefined;
    const cached =
      field === 'segment'
        ? (queryClient.getQueryData<Segment[]>(SegmentQueryOptions.getList().queryKey) ?? []).find(
            (entry) => Number(entry.id) === current.code
          )
        : (queryClient.getQueryData<Region[]>(RegionQueryOptions.getList().queryKey) ?? []).find(
            (entry) => Number(entry.id) === current.code
          );

    modal.note = pending?.note || cached?.note || '';

    if (field === 'region') {
      const regionPending = pending as Region | undefined;
      const regionCached = cached as Region | undefined;
      modal.center = regionPending?.center || regionCached?.center || '';
      modal.tzId = regionPending?.tzId ?? regionCached?.tzId ?? null;
    } else {
      modal.center = '';
      modal.tzId = null;
    }

    modal.open = true;
  }

  function upsertServerCache(field: DirectoryField, id: number, payload: DirectoryFormPayload) {
    if (field === 'segment') {
      queryClient.setQueryData(SegmentQueryOptions.getList().queryKey, (old: Segment[] | undefined) =>
        upsertListItem(old, id, { id, name: payload.name, note: payload.note || null })
      );
      return;
    }

    queryClient.setQueryData(RegionQueryOptions.getList().queryKey, (old: Region[] | undefined) =>
      upsertListItem(
        old,
        id,
        {
          id,
          name: payload.name,
          tzId: payload.tzId ?? 0,
          center: payload.center ?? '',
          note: payload.note || null,
        },
        true
      )
    );
  }

  async function refreshOptions(field: DirectoryField, selected: Option<number> | null) {
    formData.value = { ...formData.value, [field]: selected };
    await nextTick();

    const combobox = getCombobox(field);
    await combobox?.updateOptions?.();

    if (selected) {
      combobox?.choose?.(selected.code);
    }
  }

  async function syncAfterMutation(field: DirectoryField, selected: Option<number> | null) {
    if (selected && formData.value[field]?.code === selected.code) {
      await refreshOptions(field, selected);
      return;
    }

    await nextTick();
    await getCombobox(field)?.updateOptions?.();
  }

  async function getDirectoryOptions(field: DirectoryField) {
    const list =
      field === 'segment'
        ? await queryClient.ensureQueryData(SegmentQueryOptions.getList())
        : await queryClient.ensureQueryData(RegionQueryOptions.getList());

    return mergeDirectoryOptions(
      field,
      list
        .map((entry) => ({ code: Number(entry.id), label: entry.name }))
        .filter((option): option is Option<number> => Number.isFinite(option.code))
    );
  }

  async function selectDefaultOption(field: DirectoryField) {
    const state = resolveDirectoryFieldState(field, await getDirectoryOptions(field), null);
    await refreshOptions(field, state.value);
  }

  async function defaultTzId() {
    const zones = await queryClient.ensureQueryData(TimezoneQueryOptions.getList());
    const timezone = findDefaultTimezone(zones);

    if (!timezone) {
      throw new Error('The timezone list is empty.');
    }

    return timezone.id;
  }

  function closeModal() {
    modal.loading = false;
    modal.open = false;
  }

  async function upsertPending(
    field: DirectoryField,
    id: number,
    name: string,
    note: string,
    payload: DirectoryFormPayload
  ) {
    if (field === 'segment') {
      upsertPendingSegment(id, name, note);
      return;
    }

    const pending = isPendingDirectoryId(id) ? getPendingRegion(id) : undefined;
    const tzId = payload.tzId ?? pending?.tzId ?? (await defaultTzId());
    upsertPendingRegion(id, name, tzId, payload.center?.trim() ?? '', note);
  }

  async function submit(payload: DirectoryFormPayload) {
    const field = modal.field;
    const mode = modal.mode;
    const trimmed = payload.name.trim();
    const note = payload.note.trim();
    const excludeId = mode === 'edit' ? modal.itemId : null;

    if (!trimmed || modal.loading) {
      return;
    }

    modal.loading = true;

    try {
      const serverList = await fetchDirectoryList(field);
      const directoryList = [...serverList, ...getPendingDirectoryList(field)];

      if (findDirectoryNameDuplicate(directoryList, trimmed, excludeId)) {
        await getCombobox(field)?.updateOptions?.();
        return;
      }

      if (mode === 'add') {
        const id = createPendingDirectoryId();
        await upsertPending(field, id, trimmed, note, payload);
        await refreshOptions(field, { code: id, label: trimmed });
        closeModal();
        return;
      }

      const id = modal.itemId;

      if (id == null) {
        closeModal();
        return;
      }

      const selected = { code: id, label: trimmed };

      if (isPendingDirectoryId(id)) {
        await upsertPending(field, id, trimmed, note, payload);
        await syncAfterMutation(field, selected);
        closeModal();
        return;
      }

      if (field === 'segment') {
        await SegmentApi.edit(id, trimmed, note);
        upsertServerCache(field, id, { name: trimmed, note });
      } else {
        const regions = await queryClient.ensureQueryData(RegionQueryOptions.getList());
        const current = regions.find((region) => Number(region.id) === id);
        const tzId = payload.tzId ?? current?.tzId ?? (await defaultTzId());
        const center = payload.center?.trim() ?? current?.center ?? '';
        await RegionApi.edit(id, trimmed, tzId, center, note);
        upsertServerCache(field, id, { name: trimmed, note, tzId, center });
      }

      await syncAfterMutation(field, selected);
      closeModal();
    } catch (error) {
      if (ErrorUtil.isUnexpected(error)) {
        notification.error(
          mode === 'add'
            ? `counterparty.notification.error.${field}.add`
            : `counterparty.notification.error.${field}.edit`,
          error
        );
      }
    } finally {
      modal.loading = false;
    }
  }

  async function remove() {
    const field = modal.field;
    const id = modal.itemId;

    if (modal.mode !== 'edit' || id == null || modal.loading || !isPendingDirectoryId(id)) {
      return;
    }

    modal.loading = true;

    try {
      removePendingDirectory(field, id);
      closeModal();

      if (formData.value[field]?.code === id) {
        await selectDefaultOption(field);
      } else {
        await nextTick();
        await getCombobox(field)?.updateOptions?.();
      }
    } finally {
      modal.loading = false;
    }
  }

  return {
    modal,
    startAdd,
    startEdit,
    submit,
    remove,
    isNameTaken,
  };
}
